import React, { useState, useRef, useCallback } from 'react';
import { Upload, File, X, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface UploadedFile {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

interface DocumentUploadProps {
  onUpload: (files: File[]) => Promise<void>;
  acceptedFormats?: string[];
  maxFileSize?: number; // in MB
  maxFiles?: number;
  projectId?: string;
  contractId?: string;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  onUpload,
  acceptedFormats = ['.pdf', '.doc', '.docx', '.txt', '.rtf', '.xls', '.xlsx'],
  maxFileSize = 10, // 10MB default
  maxFiles = 10,
  // projectId and contractId can be used for API calls later
  projectId: _projectId,
  contractId: _contractId,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file size
    const maxSizeBytes = maxFileSize * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return `File size exceeds ${maxFileSize}MB limit`;
    }

    // Check file format
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedFormats.some(format => fileExtension === format.toLowerCase())) {
      return `File format not supported. Accepted formats: ${acceptedFormats.join(', ')}`;
    }

    // Check for potentially malicious filenames
    const dangerousPatterns = [
      /\.\./,  // Directory traversal
      /[<>:"|?*]/,  // Invalid characters
      /^\./, // Hidden files
    ];

    if (dangerousPatterns.some(pattern => pattern.test(file.name))) {
      return 'Invalid file name';
    }

    return null;
  };

  const handleFiles = useCallback((newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);

    // Check max files limit
    if (files.length + fileArray.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const validatedFiles: UploadedFile[] = fileArray.map(file => {
      const error = validateFile(file);
      return {
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        progress: 0,
        status: error ? 'error' as const : 'pending' as const,
        error: error || undefined,
      };
    });

    setFiles(prev => [...prev, ...validatedFiles]);
  }, [files.length, maxFiles, maxFileSize, acceptedFormats]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const uploadFiles = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    setUploading(true);

    // Update all pending files to uploading status
    setFiles(prev => prev.map(f =>
      f.status === 'pending' ? { ...f, status: 'uploading' as const } : f
    ));

    try {
      // Simulate upload progress for each file
      for (const uploadFile of pendingFiles) {
        // Update progress incrementally
        for (let progress = 0; progress <= 100; progress += 20) {
          setFiles(prev => prev.map(f =>
            f.id === uploadFile.id ? { ...f, progress } : f
          ));
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // Mark as successful
        setFiles(prev => prev.map(f =>
          f.id === uploadFile.id ? { ...f, status: 'success' as const, progress: 100 } : f
        ));
      }

      // Call the actual upload handler
      await onUpload(pendingFiles.map(f => f.file));
    } catch (error) {
      // Mark failed files as error
      setFiles(prev => prev.map(f =>
        pendingFiles.some(p => p.id === f.id)
          ? { ...f, status: 'error' as const, error: 'Upload failed' }
          : f
      ));
    } finally {
      setUploading(false);
    }
  };

  const clearCompleted = () => {
    setFiles(prev => prev.filter(f => f.status !== 'success'));
  };

  return (
    <div className="w-full">
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
          transition-all duration-200
          ${isDragging
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedFormats.join(',')}
          onChange={handleFileSelect}
          className="hidden"
        />

        <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />

        <p className="text-lg font-medium text-gray-700 mb-2">
          {isDragging ? 'Drop files here' : 'Drop files or click to browse'}
        </p>

        <p className="text-sm text-gray-500">
          Supported formats: {acceptedFormats.join(', ')}
        </p>

        <p className="text-sm text-gray-500">
          Maximum file size: {maxFileSize}MB • Maximum {maxFiles} files
        </p>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="mt-6 space-y-2">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Files ({files.length})
            </h3>
            {files.some(f => f.status === 'success') && (
              <button
                onClick={clearCompleted}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear completed
              </button>
            )}
          </div>

          {files.map(file => (
            <div
              key={file.id}
              className="flex items-center justify-between p-3 bg-white border rounded-lg"
            >
              <div className="flex items-center flex-1">
                <File className="h-5 w-5 text-gray-400 mr-3" />

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file.file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(file.file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  {file.error && (
                    <p className="text-xs text-red-600 mt-1">{file.error}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center ml-4">
                {file.status === 'pending' && (
                  <button
                    onClick={() => removeFile(file.id)}
                    className="p-1 hover:bg-gray-100 rounded"
                    aria-label="Remove file"
                  >
                    <X className="h-4 w-4 text-gray-400" />
                  </button>
                )}

                {file.status === 'uploading' && (
                  <div className="flex items-center">
                    <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                    <Loader className="h-4 w-4 text-blue-600 animate-spin" />
                  </div>
                )}

                {file.status === 'success' && (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                )}

                {file.status === 'error' && (
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                    <button
                      onClick={() => removeFile(file.id)}
                      className="p-1 hover:bg-gray-100 rounded"
                      aria-label="Remove file"
                    >
                      <X className="h-4 w-4 text-gray-400" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Upload Button */}
          {files.some(f => f.status === 'pending') && (
            <div className="mt-4">
              <button
                onClick={uploadFiles}
                disabled={uploading}
                className={`
                  w-full py-2 px-4 rounded-md font-medium text-white
                  ${uploading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                  }
                `}
              >
                {uploading ? 'Uploading...' : 'Upload Files'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;